/**
 * CopyButton - Reusable copy-to-clipboard button component
 * 
 * Features:
 * - Copy content to clipboard on click
 * - Visual feedback with check icon after successful copy
 * - Configurable size and styling
 * - Accessible with aria-label
 * 
 * @example
 * ```tsx
 * <CopyButton content="Text to copy" />
 * <CopyButton content={jsonString} size="sm" className="ml-2" />
 * ```
 */
import { useState, memo, useCallback } from 'react'
import { Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

export interface CopyButtonProps {
  /** The content to copy to clipboard */
  content: string
  /** Button size variant */
  size?: 'sm' | 'default' | 'icon'
  /** Additional CSS classes */
  className?: string
  /** Aria label for accessibility */
  ariaLabel?: string
  /** Icon size class (e.g., 'h-3 w-3', 'h-4 w-4') */
  iconSize?: string
}

/**
 * A button that copies content to the clipboard with visual feedback.
 * Shows a check icon for 2 seconds after successful copy.
 */
export const CopyButton = memo(function CopyButton({
  content,
  size = 'icon',
  className,
  ariaLabel = 'Copy to clipboard',
  iconSize = 'h-3 w-3',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }, [content])

  return (
    <Button
      variant="ghost"
      size={size}
      className={cn('shrink-0', className)}
      onClick={handleCopy}
      aria-label={ariaLabel}
      data-testid="copy-button"
    >
      {copied ? (
        <Check className={cn(iconSize, 'text-green-500')} data-testid="copy-success" />
      ) : (
        <Copy className={iconSize} data-testid="copy-icon" />
      )}
    </Button>
  )
})
